import type { StarsButtonProps } from '@ps/web-shared-components';
import { StarsButton } from '@ps/web-shared-components';
import React, { useCallback, useEffect } from 'react';
import type { MouseEvent } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

const DIALOG_TIMEOUT = 500;
const IOS_DEDUPLICATION_TIMEOUT = 2500;

interface MediatorMessage {
  msg: 'DOM_EVENT' | 'MEDIATOR_READY';
  key: 'blur' | 'focus' | 'visibilitychange';
  type: string;
  payload: string;
}

interface Props extends StarsButtonProps {
  href: string;
  onReturn?: Function;
  onFallback: Function;
  onIgnored: Function;
}

const getRedirectUrl = (appUrl: string) => {
  const mediatorUrl = '/native-app-link/';

  // eslint-disable-next-line stars-web/no-direct-browser-globals-access
  const { origin } = window.location;
  const url = new URL(mediatorUrl, origin);
  url.searchParams.set('url', appUrl);

  return url.toString();
};

let hasFocus = true;
let didHide = false;
let mediatorWindow: WindowProxy | null = null;

const resetFlow = () => {
  hasFocus = true;
  didHide = false;
  mediatorWindow = null;
};

const NativeAppLink: React.FC<Props> = ({ href, onReturn, onFallback, onIgnored, ...rest }) => {
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isAndroid = useSelector(select.device.isAndroid);

  const signalResult = useCallback(
    (result: 'ignored' | 'fallback' | 'return') => {
      switch (result) {
        case 'ignored':
          onIgnored?.();
          break;
        case 'fallback':
          onFallback?.();
          break;
        case 'return':
          onReturn?.();
          break;
      }
    },
    [onFallback, onIgnored, onReturn]
  );
  const handleWindowMessage = useCallback(
    (e: MessageEvent) => {
      const removeEvent = () => {
        if (mediatorWindow) {
          mediatorWindow.close();
          window.removeEventListener('message', handleWindowMessage);
          resetFlow();
        }
      };
      const msg = e.data as MediatorMessage;
      switch (msg.msg) {
        case 'MEDIATOR_READY':
          setTimeout(() => {
            if (hasFocus) {
              signalResult('ignored');
              removeEvent();
            } else if (isAndroid) {
              // android don't have popup for asking to open app
              hasFocus = true;
              if (!didHide) {
                signalResult('ignored');
                removeEvent();
              }
            }
          }, DIALOG_TIMEOUT);
          break;
        case 'DOM_EVENT':
          switch (msg.key) {
            case 'blur':
              hasFocus = false;
              break;
            case 'focus':
              if (didHide) {
                signalResult('return');
                removeEvent();
                didHide = false; // reset
              } else {
                // Ignore duplicate focus event when returning from native app on
                // iOS Safari 13.3+
                if (!hasFocus) {
                  // Wait for app switch transition to fully complete - only then is
                  // 'visibilitychange' fired
                  setTimeout(() => {
                    // If browser was not hidden, the deep link failed
                    if (!didHide) {
                      signalResult('fallback');
                      removeEvent();
                    }
                  }, IOS_DEDUPLICATION_TIMEOUT);
                }
              }
              hasFocus = true;
              break;
            case 'visibilitychange':
              if (msg.payload === 'hidden') {
                didHide = true;
              }
              break;
          }
          break;
      }
    },
    [isAndroid, signalResult]
  );

  const onClickOpenUrl = (e: MouseEvent) => {
    if (!isMobileEmbedded) {
      e.preventDefault();
      e.stopPropagation();
      mediatorWindow = window.open(getRedirectUrl(href), '_blank');
      mediatorWindow?.focus?.();

      window.addEventListener('message', handleWindowMessage);
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener('message', handleWindowMessage);
      resetFlow();
    },
    [handleWindowMessage]
  );

  return <StarsButton href={href} onClick={onClickOpenUrl} {...rest} />;
};

export default NativeAppLink;
